import { useCallback, useEffect, useRef, useState } from 'react';
import { NavArrowLeft, NavArrowRight, Plus } from 'iconoir-react';
import { cn } from 'lib/utils';
import { useTheme } from 'components/core/theme-provider';
import { useTranslation } from 'react-i18next';
import { useModelStore } from '../../../state/store/chat/use-model-store';
import { modelSupportedExtensionsMap } from '../types/model.types';
import deskSetupImage from '../../../assets/images/explore-ideas/desk-setup.jpg';
import decorationsImage from '../../../assets/images/explore-ideas/decorations.jpg';
import artImage from '../../../assets/images/explore-ideas/art.jpg';
import uiDesignImage from '../../../assets/images/explore-ideas/ui-design.jpg';

export interface ImageIdea {
  id: string;
  label: string;
  image: string;
  prompt: string;
}

const IMAGE_IDEAS: ImageIdea[] = [
  {
    id: 'desk-setup',
    label: 'Desk SetUp',
    image: deskSetupImage,
    prompt: 'Create an image of a cozy desk setup with warm ambient lighting',
  },
  {
    id: 'decorations',
    label: 'Decorations',
    image: decorationsImage,
    prompt: 'Create an image of a decorative mountain-shaped lamp with soft glow',
  },
  {
    id: 'art',
    label: 'Art',
    image: artImage,
    prompt: 'Create an image of a floating island with a waterfall in fantasy art style',
  },
  {
    id: 'ui-design',
    label: 'UI Design',
    image: uiDesignImage,
    prompt: 'Create an image of a modern mobile app UI design mockup',
  },
  {
    id: 'desk-setup',
    label: 'Desk SetUp',
    image: deskSetupImage,
    prompt: 'Create an image of a cozy desk setup with warm ambient lighting',
  },
  {
    id: 'decorations',
    label: 'Decorations',
    image: decorationsImage,
    prompt: 'Create an image of a decorative mountain-shaped lamp with soft glow',
  },
  {
    id: 'art',
    label: 'Art',
    image: artImage,
    prompt: 'Create an image of a floating island with a waterfall in fantasy art style',
  },
  {
    id: 'ui-design',
    label: 'UI Design',
    image: uiDesignImage,
    prompt: 'Create an image of a modern mobile app UI design mockup',
  },
  {
    id: 'desk-setup',
    label: 'Desk SetUp',
    image: deskSetupImage,
    prompt: 'Create an image of a cozy desk setup with warm ambient lighting',
  },
  {
    id: 'decorations',
    label: 'Decorations',
    image: decorationsImage,
    prompt: 'Create an image of a decorative mountain-shaped lamp with soft glow',
  },
  {
    id: 'art',
    label: 'Art',
    image: artImage,
    prompt: 'Create an image of a floating island with a waterfall in fantasy art style',
  },
  {
    id: 'ui-design',
    label: 'UI Design',
    image: uiDesignImage,
    prompt: 'Create an image of a modern mobile app UI design mockup',
  },
];

const CARD_WIDTH_CLASS = 'w-[96px] shrink-0 sm:w-[129px]';

const UPLOAD_CARD_CLASS = cn(
  CARD_WIDTH_CLASS,
  'flex h-[118px] flex-col items-center gap-1.5 rounded-[10px] border border-[#e7e7e7] bg-white px-2 pb-2 pt-3 sm:h-[159px] sm:gap-4 sm:rounded-[12px] sm:px-6 sm:pb-4 sm:pt-6'
);

const IDEA_CARD_CLASS = cn(
  CARD_WIDTH_CLASS,
  'relative h-[118px] overflow-hidden rounded-[10px] sm:h-[159px] sm:rounded-[12px]'
);

const IDEA_CARD_LABEL_CLASS =
  'absolute bottom-1.5 left-2 z-[1] font-sans text-[10px] font-medium not-italic leading-[12px] whitespace-nowrap text-true-white sm:bottom-2 sm:left-[9.801px] sm:text-xs sm:leading-[14.4px]';

interface ExploreIdeasSectionProps {
  onUploadPhoto: (files: File[]) => void;
  onSelectIdea?: (idea: ImageIdea) => void;
  className?: string;
}

export function ExploreIdeasSection({
  onUploadPhoto,
  onSelectIdea,
  className,
}: ExploreIdeasSectionProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const selectedModel = useModelStore((state) => state.selectedModel);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  const handleScroll = (direction: 'left' | 'right') => {
    const cardStep = scrollRef.current?.firstElementChild?.clientWidth ?? 129;
    scrollRef.current?.scrollBy({
      left: direction === 'left' ? -(cardStep + 8) : cardStep + 8,
      behavior: 'smooth',
    });
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length) {
      return;
    }

    onUploadPhoto(Array.from(event.target.files));
    event.target.value = '';
  };

  const acceptImages = selectedModel
    ? modelSupportedExtensionsMap[selectedModel.modelType].image.join(',')
    : 'image/*';

  const iconColor = theme === 'dark' ? '#F6F6F6' : '#3D3D3D';
  const headerTextClass = theme === 'dark' ? 'text-[#F6F6F6]' : 'text-[#3D3D3D]';

  useEffect(() => {
    updateScrollState();
    window.addEventListener('resize', updateScrollState);
    return () => window.removeEventListener('resize', updateScrollState);
  }, [updateScrollState]);

  return (
    <div className={cn('flex w-full min-w-0 flex-col gap-3', className)}>
      {/* Header — Figma 1585:20907 */}
      <div className="flex w-full min-w-0 items-center justify-between gap-3">
        {/* Title — Figma 1585:20908 */}
        <h3
          className={cn(
            'shrink-0 font-sans text-lg font-medium not-italic leading-[27px] md:text-xl md:leading-[30px]',
            headerTextClass
          )}
        >
          {t('EXPLORE_IDEAS', { defaultValue: 'Explore ideas' })}
        </h3>

        {/* Actions — Figma 1585:20909 */}
        <div className="hidden shrink-0 items-center gap-2 md:flex md:gap-2.5">
          <button
            type="button"
            className={cn(
              'inline-flex items-center whitespace-nowrap font-sans text-sm font-medium not-italic leading-[16.8px] underline decoration-solid decoration-skip-ink-none [text-underline-position:from-font]',
              headerTextClass
            )}
          >
            {t('WHATS_NEW', { defaultValue: "What's new" })}
          </button>

          <div className="flex items-center gap-0.5">
            <button
              type="button"
              aria-label={t('SCROLL_LEFT', { defaultValue: 'Scroll left' })}
              disabled={!canScrollLeft}
              onClick={() => handleScroll('left')}
              className="flex size-6 items-center justify-center disabled:opacity-30"
            >
              <NavArrowLeft width={24} height={24} color={iconColor} />
            </button>
            <button
              type="button"
              aria-label={t('SCROLL_RIGHT', { defaultValue: 'Scroll right' })}
              disabled={!canScrollRight}
              onClick={() => handleScroll('right')}
              className="flex size-6 items-center justify-center disabled:opacity-30"
            >
              <NavArrowRight width={24} height={24} color={iconColor} />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={scrollRef}
        onScroll={updateScrollState}
        className="flex w-full min-w-0 gap-1.5 overflow-x-auto overscroll-x-contain pb-1 sm:gap-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <button
          type="button"
          onClick={handleUploadClick}
          className={cn(
            UPLOAD_CARD_CLASS,
            theme === 'dark' && 'border-gray-500 bg-black-400'
          )}
        >
          <span className="flex min-h-0 w-full flex-1 items-center justify-center">
            <span className="flex size-6 items-center justify-center overflow-hidden rounded-full p-1 sm:size-8 sm:p-[7px]">
              <Plus width={20} height={20} color={iconColor} />
            </span>
          </span>
          <span className="shrink-0 text-center text-[10px] leading-[1.2] text-[#3d3d3d] dark:text-foreground sm:whitespace-nowrap sm:text-xs">
            {t('UPLOAD_A_PHOTO', { defaultValue: 'Upload a photo' })}
          </span>
        </button>

        {IMAGE_IDEAS.map((idea) => (
          <button
            key={idea.id}
            type="button"
            onClick={() => onSelectIdea?.(idea)}
            className={IDEA_CARD_CLASS}
          >
            <img
              src={idea.image}
              alt={idea.label}
              aria-hidden
              className="pointer-events-none absolute inset-0 size-full object-cover"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[rgba(102,102,102,0.2)] to-[rgba(0,0,0,0.3)]"
            />
            <span className={IDEA_CARD_LABEL_CLASS}>
              {idea.label}
            </span>
          </button>
        ))}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={acceptImages}
        multiple
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
}
